import React, { useState, useEffect } from 'react'
import type { Module, CreateModuleDTO, UpdateModuleDTO } from '@/types/module'

interface ModuleFormData {
  title: string
  description: string
  durationHours: number
  isPrerequisite: boolean
}

interface ModuleFormModalProps {
  courseId: string
  module?: Module | null
  onSave: (data: CreateModuleDTO | UpdateModuleDTO) => Promise<void>
  onClose: () => void
  isSaving: boolean
  error?: string | null
}

export const ModuleFormModal: React.FC<ModuleFormModalProps> = ({
  courseId,
  module,
  onSave,
  onClose,
  isSaving,
  error,
}) => {
  const [formData, setFormData] = useState<ModuleFormData>({
    title: '',
    description: '',
    durationHours: 0,
    isPrerequisite: false,
  })
  const [errors, setErrors] = useState<Partial<Record<keyof ModuleFormData, string>>>({})

  useEffect(() => {
    if (module) {
      setFormData({
        title: module.title || '',
        description: module.description || '',
        durationHours: module.durationHours || 0,
        isPrerequisite: module.isPrerequisite || false,
      })
    }
  }, [module])

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ModuleFormData, string>> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (formData.durationHours < 0) {
      newErrors.durationHours = 'Duration must be positive'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    if (module) {
      // Update existing module
      const updateData: UpdateModuleDTO = {
        title: formData.title,
        description: formData.description,
        durationHours: formData.durationHours,
        isPrerequisite: formData.isPrerequisite,
      }
      await onSave(updateData)
    } else {
      // Create new module
      const createData: CreateModuleDTO = {
        courseId,
        title: formData.title,
        description: formData.description,
        durationHours: formData.durationHours,
        isPrerequisite: formData.isPrerequisite,
      }
      await onSave(createData)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-white/10">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/5 bg-white dark:bg-gray-800 rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {module ? 'Edit Module' : 'Add New Module'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-red-400 text-lg shrink-0">error</span>
                <div>
                  <p className="text-sm font-medium text-red-400">Failed to save module</p>
                  <p className="text-xs text-red-400/80 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Module Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              className={`w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-white/5 border text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary ${errors.title ? 'border-red-500' : 'border-gray-200 dark:border-white/10'
                }`}
              placeholder="e.g., Introduction to Programming"
            />
            {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Brief description of what this module covers..."
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Estimated Duration (hours)
            </label>
            <input
              type="number"
              min="0"
              value={formData.durationHours}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, durationHours: parseInt(e.target.value) || 0 }))
              }
              className={`w-32 px-4 py-3 rounded-lg bg-gray-100 dark:bg-white/5 border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary ${errors.durationHours ? 'border-red-500' : 'border-gray-200 dark:border-white/10'
                }`}
            />
            {errors.durationHours && (
              <p className="mt-1 text-sm text-red-400">{errors.durationHours}</p>
            )}
          </div>

          {/* Is Prerequisite Toggle */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPrerequisite}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isPrerequisite: e.target.checked }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-6 rounded-full transition-colors ${formData.isPrerequisite ? 'bg-primary' : 'bg-gray-100 dark:bg-white/10'
                  }`}
              >
                <div
                  className={`w-4 h-4 rounded-full bg-white transition-transform mt-1 ${formData.isPrerequisite ? 'translate-x-5 ml-0' : 'translate-x-1'
                    }`}
                />
              </div>
              <div>
                <span className="text-sm text-gray-900 dark:text-white">Mark as prerequisite</span>
                <p className="text-xs text-gray-500 dark:text-text-muted">Must be completed before other modules</p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-white/5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[18px]">save</span>
                  {module ? 'Save Changes' : 'Create Module'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ModuleFormModal
